/**
 * SearchButton — SVG-morphing search/close toggle.
 *
 * Cross-fade + scale/rotate between magnifier and X icons.
 * Button background morphs transparent → filled.
 *
 * Opening: ~380ms  |  Closing: ~280ms (intentionally snappier)
 */

import { useState, useEffect, useRef } from 'react';
import { T } from '@/styles/tokens';

export const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';

export const SEARCH_TIMING = {
  iconMorph: 380,
  iconClose: 280,
  overlayIn: 340,
  overlayOut: 280,
  stagger: 60,
};

interface SearchButtonProps {
  isOpen: boolean;
  onClick: () => void;
  variant?: 'dark' | 'light';
}

export function SearchButton({ isOpen, onClick, variant = 'dark' }: SearchButtonProps) {
  const [pressed, setPressed] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevOpen = useRef(isOpen);

  useEffect(() => {
    if (prevOpen.current !== isOpen) {
      setIsAnimating(true);
      const dur = isOpen ? SEARCH_TIMING.iconMorph : SEARCH_TIMING.iconClose;
      const timer = setTimeout(() => setIsAnimating(false), dur);
      prevOpen.current = isOpen;
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClick = () => {
    if (isAnimating) return;
    onClick();
  };

  const isLight = variant === 'light';
  const strokeColor = isLight ? '#FFFFFF' : T.textSecondary;
  const bgColor = isOpen
    ? (isLight ? 'rgba(255,255,255,0.18)' : T.text)
    : 'transparent';
  const shadow = isOpen ? '0 2px 12px rgba(11,12,19,0.25)' : 'none';
  const dur = isOpen ? SEARCH_TIMING.iconMorph : SEARCH_TIMING.iconClose;

  return (
    <button
      onClick={handleClick}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      aria-label={isOpen ? 'Close search' : 'Search portfolio'}
      style={{
        width: 44, height: 44, borderRadius: 22,
        background: bgColor,
        boxShadow: shadow,
        border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transform: pressed ? 'scale(0.88)' : 'scale(1)',
        transition: `background ${dur}ms ${EASE_OUT}, box-shadow ${dur}ms ${EASE_OUT}, transform 120ms ${EASE_OUT}`,
        position: 'relative',
        zIndex: 52,
      }}
    >
      <SearchMorphIcon isOpen={isOpen} strokeColor={strokeColor} dur={dur} />
    </button>
  );
}

/**
 * Shared morphing SVG — cross-fade between magnifier and X icons.
 * Uses standard CSS opacity/scale/rotate (no SVG2 geometry properties).
 */
export function SearchMorphIcon({
  isOpen,
  strokeColor,
  dur = SEARCH_TIMING.iconMorph,
}: {
  isOpen: boolean;
  strokeColor: string;
  dur?: number;
}) {
  return (
    <div style={{ width: 20, height: 20, position: 'relative' }}>
      {/* Magnifying glass — visible when closed */}
      <svg
        width="20" height="20" viewBox="0 0 24 24" fill="none"
        style={{
          position: 'absolute', top: 0, left: 0,
          opacity: isOpen ? 0 : 1,
          transform: isOpen ? 'scale(0.6) rotate(-90deg)' : 'scale(1) rotate(0deg)',
          transition: `opacity ${Math.round(dur * 0.5)}ms ${EASE_OUT}, transform ${dur}ms ${EASE_OUT}`,
        }}
      >
        <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke={strokeColor} strokeWidth="2.2" />
        <line x1="15.5" y1="15.5" x2="20" y2="20" stroke={strokeColor} strokeWidth="2.2" strokeLinecap="round" />
      </svg>
      {/* X icon — visible when open */}
      <svg
        width="20" height="20" viewBox="0 0 24 24" fill="none"
        style={{
          position: 'absolute', top: 0, left: 0,
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? 'scale(1) rotate(0deg)' : 'scale(0.6) rotate(90deg)',
          transition: `opacity ${Math.round(dur * 0.5)}ms ${EASE_OUT} ${isOpen ? Math.round(dur * 0.15) : 0}ms, transform ${dur}ms ${EASE_OUT}`,
        }}
      >
        <line x1="7" y1="7" x2="17" y2="17" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" />
        <line x1="17" y1="7" x2="7" y2="17" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" />
      </svg>
    </div>
  );
}
